// Service d'authentification pour CHAUFFEURS
// Utilise l'API backend avec JWT tokens
import apiService from './apiService';
import { LEGAL_VERSION } from './LegalGate';

const authServiceDriver = {
  // Inscription
  register: async (userData) => {
    try {
      const response = await apiService.post('/auth/register/driver', {
        name: userData.name,
        phone: userData.phone,
        email: userData.email || null,
        password: userData.password,
        photo: userData.photo,
        idDocument: userData.idDocument,
        vehicle: userData.vehicle,
        vehiclePhotos: userData.vehiclePhotos || [],
        primary_route: userData.primary_route || null,
        secondary_route: userData.secondary_route || null,
        terms_version: LEGAL_VERSION,
        phone_verification_token: userData.phone_verification_token, // V7 : code WhatsApp confirmé avant la création
        ...(userData.bot || {}), // piège, durée de remplissage, jeton captcha (BotShield)
      }, { userType: 'driver' });  // Utilise le timeout par défaut de 30s

      if (response.success) {
        apiService.setToken(response.token, 'driver');
        localStorage.setItem('currentDriver', JSON.stringify(response.user));
        return { success: true, user: response.user, phone_verified: response.phone_verified };
      }

      return response;
    } catch (error) {
      return {
        success: false,
        error: error.error || 'Erreur lors de l\'inscription'
      };
    }
  },

  // Connexion
  login: async (phone, password) => {
    try {
      const response = await apiService.post('/auth/login/driver', {
        phone,
        password
      }, { userType: 'driver' });


      if (response.success) {
        apiService.setToken(response.token, 'driver');
        localStorage.setItem('currentDriver', JSON.stringify(response.user));
        
        // Si le statut est "pending", retourner aussi cette info
        // V7 : phone_verified false → compte créé sans code (ancienne app) → la page demande le code
        if (response.status === 'pending') {
          return { success: true, user: response.user, status: 'pending', phone_verified: response.phone_verified };
        }

        return { success: true, user: response.user, phone_verified: response.phone_verified };
      }

      console.warn('⚠️ Réponse non-success:', response);
      return response;
    } catch (error) {
      console.error('❌ Erreur connexion:', error);
      return {
        success: false,
        error: error.message || error.error || 'Erreur lors de la connexion: ' + JSON.stringify(error)
      };
    }
  },

  // ── Connexion par téléphone (2026-10-30) ──────────────────────────────────────
  // Réponse de /auth/otp-login (compte utilisateur) → session chauffeur via /auth/driver-session,
  // ou { needsOnboarding: true, user } (compte sans profil chauffeur → « Devenir chauffeur »)
  persistOtpSession: async (resp) => {
    if (!resp?.success || !resp.token) return resp;
    apiService.setToken(resp.token, 'user');
    localStorage.setItem('currentUser', JSON.stringify(resp.user));
    if (resp.refresh_token) apiService.setRefreshToken(resp.refresh_token);
    return authServiceDriver.exchangeDriverSession(resp.user);
  },
  exchangeDriverSession: async (user) => {
    try {
      const sess = await apiService.post('/auth/driver-session', {}, { userType: 'user' });
      if (sess?.success && sess.token) {
        const driver = { ...sess.user, status: sess.status || sess.user?.status };
        apiService.setToken(sess.token, 'driver');
        localStorage.setItem('currentDriver', JSON.stringify(driver));
        return { success: true, user: driver, status: driver.status };
      }
      return { success: false, error: sess?.error || 'Session chauffeur impossible' };
    } catch (e) {
      if (e?.status === 404 || e?.data?.needs_onboarding) return { success: true, needsOnboarding: true, user };
      return { success: false, error: e?.error || e?.message || 'Session chauffeur impossible' };
    }
  },
  // « Devenir chauffeur » : crée le profil chauffeur du compte connecté (jeton utilisateur)
  onboard: async (payload) => {
    try {
      const res = await apiService.post('/drivers/onboard', { ...payload, terms_version: LEGAL_VERSION }, { userType: 'user' });
      if (res?.success && res.token) {
        const driver = { ...res.user, status: res.status || res.user?.status };
        apiService.setToken(res.token, 'driver');
        localStorage.setItem('currentDriver', JSON.stringify(driver));
        return { success: true, user: driver, status: driver.status };
      }
      return { success: false, error: res?.error || 'Inscription impossible' };
    } catch (e) {
      return { success: false, error: e?.error || e?.message || 'Inscription impossible' };
    }
  },
  hasRefresh: () => !!apiService.getRefreshToken(),
  restoreSession: async () => {
    const ok = await apiService.silentRefresh('driver');
    const user = ok ? authServiceDriver.getCurrentUser() : null;
    return ok && user ? { success: true, user } : { success: false };
  },

  // Déconnexion (coupe aussi le jeton d'appareil de ce navigateur, sans bloquer)
  logout: () => {
    const rt = apiService.getRefreshToken();
    if (rt) apiService.post('/auth/logout-device', { refresh_token: rt }).catch(() => {});
    apiService.removeRefreshToken();
    localStorage.removeItem('currentDriver');
    localStorage.removeItem('currentUser');
    apiService.removeToken('driver');
    apiService.removeToken('user');
    return { success: true };
  },

  // Vérifier si connecté
  isAuthenticated: () => {
    return !!apiService.getToken('driver') && !!localStorage.getItem('currentDriver');
  },

  // Récupérer le chauffeur actuel
  getCurrentUser: () => {
    const driver = localStorage.getItem('currentDriver');
    return driver ? JSON.parse(driver) : null;
  },

  // Mot de passe oublié - Étape 1 : envoyer le code — V7 : options { channel: 'auto' | 'email', language }
  forgotPassword: async (phone, options = {}) => {
    try {
      const response = await apiService.post('/auth/forgot-password/driver', { phone, ...options }, { userType: 'driver' });
      return response;
    } catch (error) {
      return { success: false, error: error.message || 'Erreur lors de l\'envoi du code' };
    }
  },

  // Mot de passe oublié - Étape 2 : Vérifier le code
  verifyResetCode: async (phone, code) => {
    try {
      const response = await apiService.post('/auth/verify-reset-code/driver', { phone, code }, { userType: 'driver' });
      return response;
    } catch (error) {
      return { success: false, error: error.message || 'Code incorrect' };
    }
  },

  // Mot de passe oublié - Étape 3 : Réinitialiser le mot de passe
  resetPassword: async (phone, code, newPassword) => {
    try {
      const response = await apiService.post('/auth/reset-password/driver', { phone, code, newPassword }, { userType: 'driver' });
      return response;
    } catch (error) {
      return { success: false, error: error.message || 'Erreur lors de la réinitialisation' };
    }
  }
};

export default authServiceDriver;